import React from "react";
import { format } from "date-fns";

// MeetingRow — reskinned to the canonical My-Meetings row (design frame 5d):
// a left time-block (start time + duration), the meeting identity (topic + a
// type line with a video glyph, since platform meetings are Zoom/video), and a
// trailing action — a primary "Join" for an upcoming meeting, a secondary
// "Details" otherwise. Selecting the row still opens the detail pane; Join is a
// direct shortcut into the call (host → start_url, guest → join_url), matching
// the detail pane's own join logic so the two never diverge.
//
// Honest 5d note: the frame's row is person-first (a named counterpart + role
// pill). The platform meeting model exposes the host (schedule_by) and the
// topic, not a distinct counterpart participant, so the row is topic-first with
// the host on the type line — real fields only, nothing invented.
const MeetingRow = ({ meeting, isSelected, isPast, currentEmail, onClick, onJoin }) => {
  const start = new Date(meeting.schedule_date);
  const time = format(start, "HH:mm");
  const duration = meeting.duration ? `${meeting.duration} min` : "";
  const hostName = meeting.schedule_by?.name || meeting.schedule_by?.email || "";
  const upcoming = !isPast;

  // Join target mirrors MeetingCard: the host opens the start_url, a guest the
  // join_url.
  const isHost =
    currentEmail && meeting.schedule_by?.email
      ? meeting.schedule_by.email === currentEmail
      : false;
  const joinUrl = isHost ? meeting.start_url : meeting.join_url;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-[12px] px-2 py-2 text-left transition-colors ${
        isSelected ? "bg-[#F0F6FF] shadow-[inset_2px_0_0_#2283FF]" : "hover:bg-gray-50"
      }`}
    >
      {/* Left time-block (5d). Upcoming-today reads blue; later/past reads grey. */}
      <div
        className="flex flex-shrink-0 flex-col items-center justify-center"
        style={{
          width: 52,
          height: 52,
          borderRadius: 10,
          background: upcoming ? "#F0F6FF" : "#F8FAFC",
          border: upcoming ? "1px solid #D6E6FF" : "1px solid #EEF1F5",
        }}
      >
        <span
          style={{ fontSize: 14, fontWeight: 700, color: upcoming ? "#1466D8" : "#475467" }}
        >
          {time}
        </span>
        {duration && (
          <span style={{ fontSize: 9.5, fontWeight: 600, color: "#98A2B3" }}>
            {duration}
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[13.5px] font-semibold text-[#101828]">
          {meeting.topic}
        </span>
        <span className="flex items-center gap-1.5 truncate text-[11.5px] text-[#667085]">
          {/* Video glyph — platform meetings run over Zoom video. */}
          <svg width="13" height="10" viewBox="0 0 20 14" fill="none" className="flex-shrink-0">
            <path
              d="M14 5.2l4.4-3.1c.4-.3 1 0 1 .5v8.8c0 .5-.6.8-1 .5L14 8.8M2.5 1h9A2.5 2.5 0 0114 3.5v7A2.5 2.5 0 0111.5 13h-9A2.5 2.5 0 010 10.5v-7A2.5 2.5 0 012.5 1z"
              stroke="#98A2B3"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {["Video", hostName].filter(Boolean).join(" · ")}
        </span>
      </div>

      {/* Trailing action: Join (upcoming, primary) or Details (secondary). */}
      {upcoming && joinUrl ? (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onJoin?.(meeting);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              onJoin?.(meeting);
            }
          }}
          className="flex-shrink-0 rounded-[8px] bg-[#2283FF] px-3 py-1.5 text-[12px] font-semibold text-white"
        >
          Join
        </span>
      ) : (
        <span className="flex-shrink-0 rounded-[8px] border border-[#E4E9F0] px-3 py-1.5 text-[12px] font-semibold text-[#475467]">
          Details
        </span>
      )}
    </button>
  );
};

export default React.memo(MeetingRow);
